import { EventEmitter, Inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { AuthWellKnownEndpoints, OidcSecurityService, OpenIDImplicitFlowConfiguration } from 'angular-auth-oidc-client';
import { BehaviorSubject, of } from 'rxjs';
import { delay, filter, map, skipWhile, take, takeUntil, tap } from 'rxjs/operators';
import { NdmsResolverService } from 'projects/shared/core/interfaces/ndmsResolverService';
import { AuthenticationService } from 'projects/jpats/app/api/services/authentication.service';
import { NdmsUserManagementService } from 'projects/shared/core/app/api/services/ndms-user-management.service';
import { UserService } from 'projects/shared/core/app/api/services/user.service';
import { NdmsTokenRequest } from 'projects/shared/core/app/api/models/ndms-token-request';
import { NdmsJsonWepAuthResponse } from 'projects/shared/core/app/api/models/ndms-json-wep-auth-response';

@Injectable()
export class NdmsAuthService extends NdmsResolverService {
  private _bearerToken$ = new BehaviorSubject('');
  get bearerToken$() {
    return this._bearerToken$.asObservable();
  }

  isAuthorized = false;
  authFinished: BehaviorSubject<any> = new BehaviorSubject<any>({});
  appAuthFinished: EventEmitter<any> = new EventEmitter<any>();
  resetPatientSearch: EventEmitter<any> = new EventEmitter<any>();
  showLocationSelect: EventEmitter<any> = new EventEmitter<any>();

  configUrl: string = '';
  authData: any;
  appAuthData: any;
  baseUrl: string = '';
  identityUrl: string = '';
  refresher: any = undefined;
  public static externalData: any;
  resolve(params: any): any {
    return undefined;
  }

  constructor(
    @Inject('BASE_URL') baseUrl: string,
    @Inject('IDENTITY_URL') identityUrl: string,
    public oidcSecurityService: OidcSecurityService,
    private _http: HttpClient,
    private _router:Router,
    private _authenticationService: AuthenticationService,
    private _ndmsUserService: UserService,
    private _ndmsUserManagementService: NdmsUserManagementService) {
    super();

    const openIdImplicitFlowConfiguration = new OpenIDImplicitFlowConfiguration();
    openIdImplicitFlowConfiguration.stsServer = identityUrl;
    this.configUrl = identityUrl;
    openIdImplicitFlowConfiguration.redirect_url = baseUrl + '/';
    openIdImplicitFlowConfiguration.client_id = 'ndms';
    openIdImplicitFlowConfiguration.response_type = 'id_token token';
    openIdImplicitFlowConfiguration.scope = 'openid profile ndms';
    openIdImplicitFlowConfiguration.post_logout_redirect_uri = baseUrl + 'F/';
    openIdImplicitFlowConfiguration.forbidden_route = '/forbidden';
    openIdImplicitFlowConfiguration.unauthorized_route = '/';
    openIdImplicitFlowConfiguration.auto_userinfo = true;
    openIdImplicitFlowConfiguration.log_console_warning_active = false;
    openIdImplicitFlowConfiguration.log_console_debug_active = false;
    openIdImplicitFlowConfiguration.max_id_token_iat_offset_allowed_in_seconds = 400;
    openIdImplicitFlowConfiguration.silent_renew = true;
    openIdImplicitFlowConfiguration.silent_renew_url = baseUrl + '/silent-renew.html';
    openIdImplicitFlowConfiguration.silent_redirect_url= baseUrl + '/silent-renew.html';
    const authWellKnownEndpoints = new AuthWellKnownEndpoints();
    authWellKnownEndpoints.issuer = identityUrl;
// + '/.well-known/openid-configuration/jwks'
    authWellKnownEndpoints.jwks_uri =
      identityUrl+ '/.well-known/openid-configuration/jwks';
    authWellKnownEndpoints.authorization_endpoint = identityUrl + '/connect/authorize';
    authWellKnownEndpoints.token_endpoint = identityUrl + '/connect/token';
    authWellKnownEndpoints.userinfo_endpoint = identityUrl + '/connect/userinfo';
    authWellKnownEndpoints.end_session_endpoint = identityUrl + '/connect/endsession';
    authWellKnownEndpoints.check_session_iframe =
      identityUrl + '/connect/checksession';
    authWellKnownEndpoints.revocation_endpoint = identityUrl + '/connect/revocation';
    authWellKnownEndpoints.introspection_endpoint =
      identityUrl + '/connect/introspect';

    this.identityUrl = identityUrl;
    this.baseUrl = baseUrl + '/';

    this.oidcSecurityService.getIsAuthorized().pipe(
      tap((isAuthorized: boolean) => {
        this.isAuthorized = isAuthorized;
        if(!this.isAuthorized){
          console.log("Im not authenticated, redirecting", this._router.url);
          this.setRedirectUrl(this._router.url);
        }
      })
    );
    this.oidcSecurityService.getIsModuleSetup().pipe(
      filter((isModuleSetup: boolean) => isModuleSetup),
      take(1)
    ).subscribe((isModuleSetup: boolean) => {
      this.doCallbackLogicIfRequired();
    });
    this.oidcSecurityService.setupModule(openIdImplicitFlowConfiguration, authWellKnownEndpoints);
  }

  private doCallbackLogicIfRequired() {
    if (typeof location !== "undefined" && window.location.hash) {
      this.oidcSecurityService.authorizedImplicitFlowCallback();
    }
    this.loadAuth();
  }

  loadAuth() {
    let token = this.getServerToken();
    if (!token || token === "") {
      return this.login();
    }

    this.getExternalData(token).subscribe(async data => {
      this.isAuthorized = true;
      (await this.authenticate()).subscribe(async res => {
        if (res.model && res.model!.authenticated && res.model!.jwt) {
          this.authFinished.next(data);
          if (this.getRedirect() && this.getRedirect() !== "/") {
            await this._router.navigate([ this.getRedirect() ]);
            this.clearRedirect();
          }
        }
      });
    }, err => this.login())
  }
  
  getExternalData(token) {
    return this._http.get(this.configUrl + '/api/userprofile', this.getOptions(token)).pipe();
  }
  getIdentityData() {
    let token = this.getServerToken();
    return this._http.get(this.configUrl + '/api/accounts/GetCurrent', this.getOptions(token)).pipe();
  }
  getUserApps(){
    return this._ndmsUserManagementService.GetCurrentUser().pipe(tap());
  }
  getAllApps() {
    return this._ndmsUserManagementService.AllApps().pipe(map(res => {
      return res;
    }));
  }

  addAppWithJustification(app) {
    return this._ndmsUserManagementService.AddApp(app).pipe(tap());
  }
  getOptions(token) {
    let options = {
      headers:
        new HttpHeaders({ 'Content-Type': 'application/json' }),
      params: {}
    };
    options.headers = options.headers.set('Authorization', 'Bearer ' + token);
    return options;
  }
  externalUserData(){
    return this._http.get(this.configUrl + '/api/userprofile', { headers: new HttpHeaders(
      {
        'Content-Type':  'application/json',
        'Authorization' : "Bearer " + this.getServerToken()
      }
    )}).pipe();
  }
  saveApps(apps) {
    return this._http.post(this.configUrl + '/api/userprofile',apps, { headers:new HttpHeaders(
      { Authorization: "Bearer " + this.getServerToken(), 'Access-Control-Allow-Origin': '*' }
    )}).pipe();
  }
  saveSelectedApps(appKeys, selectedApp, accountJustification) {
    return this._http.post(this.configUrl + '/api/userprofile',appKeys, { headers:new HttpHeaders(
      { Authorization: "Bearer " + this.getServerToken(), 'Access-Control-Allow-Origin':'*' }
    )}).pipe();
  }
  
  allApps(){
    return this._http.get(this.configUrl + '/user/userprofile/allapps', { headers: new HttpHeaders(
      { Authorization: "Bearer " + this.getServerToken() }
    )}).pipe();
  }
  initiateLoginFlow() {
    this.oidcSecurityService.onAuthorizationResult.subscribe(res => {
    }, error => this.logout(true));
  }
  login() {
    console.log("Im not authenticated, redirecting", this._router.url);
    this.oidcSecurityService.authorize();
  }
  refreshSession() {
    this.oidcSecurityService.authorize();
  }
  logout(clearRedirect: boolean) {
    if (clearRedirect) {
      this.clearRedirect();
    }
    this.clearToken();
    this.oidcSecurityService.logoff();
  }
  hasAuthenticated(): boolean {
    return ((window.sessionStorage.getItem("_isAuthorized_ndms")  || false)) as boolean;
  }
  
  getServerToken() {
    let authResult = window.sessionStorage.getItem("authorizationResult_ndms");
    return JSON.parse(authResult || "{}").id_token || "";
  }
  setServerToken(token) {
    window.sessionStorage.setItem("authorizationResult_ndms", token);
  }
  getLoginInformation() {
    return this._http.get(this.baseUrl + 'view/ndms/user/GetLoginInformation', { headers: new HttpHeaders(
      { Authorization: "Bearer " + this.getServerToken() }
    )}).pipe();
  }
  getUsername() {
    return new Promise<string>((resolve, reject) => {
      let name = JSON.parse(window.sessionStorage.getItem("userData_ndms") || "{}").name;
      if (name) {
        return resolve(name);
      }
      this.oidcSecurityService.getUserData()
      .pipe(skipWhile(v => !v))
      .pipe(takeUntil(of(true).pipe(delay(5000))))
      .toPromise()
      .then(userData => resolve(userData.name))
      .catch(err => console.log("oidcSecurityService.getUserData() - error", reject(err)))
      .finally(() => {
        let userData = window.sessionStorage.getItem("userData_ndms");
        if (!userData || userData === '""' || userData === '{}') {
          this.logout(true);
        }
      });
    });
  }
  
  getToken(): string | null {
    return window.localStorage.getItem("token");
  }
  setToken(token: string) {
    window.localStorage.setItem("token","Bearer " + token);
    if (token && token.trim() !== '') {
      this._bearerToken$.next(this.getToken());
    }
  }
  isValidToken(value) {   
    return value && value.trim() !== '' && value.trim() !== "Bearer";
  }
  clearToken(){
    this._bearerToken$.next("Bearer ");
    window.localStorage.removeItem("token");
    window.sessionStorage.clear();
  }

  async refreshToken() {
    let userName = await this.getUsername();
    let request: NdmsTokenRequest = { username: userName, stsJwt: this.getServerToken() };
    return this._ndmsUserManagementService.RefreshNdmsToken(request).pipe(map(res => {
      this.setToken(res.model!.jwt || "");
      return res;
    }));
  }
  async authenticate() {
    let userName = await this.getUsername();
    return this._ndmsUserManagementService.RequestNdmsToken({ username: userName, stsJwt: this.getServerToken() }).pipe(map(res => {
      console.log("authenticate() - RequestNdmsToken()", res);
      this.authData = res;
      if (res.model.statusCode === 440) {
        this.login();
      }
      this.setToken(res.model!.jwt || "");
      return res;
    }));
  }
  
  async refreshTokenApp(){
    let userName = await this.getUsername();
    return this._authenticationService.RefreshToken({ stsJwt:this.getServerToken(), username:userName }).pipe(map(res => {
      this.setToken(res.model!.jwt || "");
      return res;
    }));
  }

  async authenticateApp() {
    let userName = await this.getUsername();
    return this._authenticationService.RequestToken({ stsJwt:this.getServerToken(), username:userName }).pipe(map(res => {
      this.appAuthData = res;
      if(res.model.statusCode === 440){
        this.login();
      }
      this.setToken(res.model!.jwt || "");
      let redirectUrl = this.getRedirect();
      if(redirectUrl) {
        this._router.navigateByUrl(redirectUrl);
      }
      else {
        this.appAuthFinished.emit(res.model);
      }
      return res;
    }));
  }

  saveAccessJustification(accountInfo) {
    this.authData.model.user.accountJustification = accountInfo.accountJustification;
    return this._authenticationService.SendNewAccountNotification(accountInfo).pipe(map(res => {
      return res;
    }));
  }
  
  setRedirectUrl(url:string){
    console.log("setRedirectUrl", url);
    if(url && (url === "/" || url === "")) {
      return;
    }
    window.localStorage.setItem("redirect_url", url);
  }

  getRedirect(){
    let url = window.localStorage.getItem("redirect_url");
    return url;
  }

  clearRedirect() {
    window.localStorage.removeItem("redirect_url");
  }

  setRedirect(route: ActivatedRouteSnapshot) {
    let url = this.getResolvedUrl(route);
    this.setRedirectUrl(url);
  }

  getResolvedUrl(route: ActivatedRouteSnapshot): string {
    return route.pathFromRoot.map(v => v.url.map(segment => segment.toString()).join('/')).join('/');
  }

  requestToken() {
    return undefined;
  }
  
}
